/** Does this URL name a route the app knows how to open?
 *
 *  Answers one question, asked ONCE at boot against the entry path: did the
 *  user arrive somewhere real. It is not a router and must never be used to
 *  decide what to render — in-app navigation deliberately leaves the URL
 *  behind (opening Messages from a project keeps the project's path), so a URL
 *  checked mid-session says nothing about the current view and would produce
 *  false negatives.
 *
 *  Before this existed an unknown path was silently swallowed: nothing matched,
 *  nothing threw, and the app rendered whatever view was left in localStorage
 *  from last time. A mistyped or revoked link looked like it had worked, which
 *  is worse than a plain 404 — the user had no way to tell they were not where
 *  they meant to be.
 *
 *  A miss here is expensive in one direction: marking a REAL route unknown
 *  404s a page that works. So the table is deliberately generous — it matches
 *  the SHAPE of a route, not whether the record behind it exists. A path that
 *  is well-formed but names a deleted project resolves later, against the
 *  data, and surfaces its own not-found.
 */

import { APP_VIEWS } from '../app/navigation';
import { registeredSubRoutePatterns } from './subRoutes';
import { parsePrettyProjectEventsPath, parsePrettyProjectInterimPath, parsePrettyProjectTabPath } from './prettyRoutes';

/** Every view is addressable as `/${view}` — AuthenticatedController builds the
 *  URL that way for anything without a prettier form (see its `let url =`).
 *  Derived from the same list rather than hand-listed, because hand-listing is
 *  exactly how this went wrong the first time: the table was written from the
 *  ad-hoc `pathname.startsWith` checks in App.tsx and missed ~45 views, so a
 *  refresh on /email or /calendar would have 404'd a page that works. */
const VIEW_ROUTES: ReadonlySet<string> = new Set(APP_VIEWS.map(view => `/${view}`));

/** Exact paths, no parameters. */
const STATIC_ROUTES: ReadonlySet<string> = new Set([
  '/',
  // Auth + onboarding
  '/login',
  '/register',
  '/start',
  '/signup',
  '/join',
  '/logout',
  // Directories the sidebar links to
  '/projects',
  '/workspaces',
  // Both spellings: the sidebar links one, AuthenticatedController writes the
  // other (`/workspaces/add-workspace`).
  '/add-workspace',
  '/workspaces/add-workspace',
  '/employers',
  '/employers/add',
  '/workspace/resource-library',
  // Legacy bookmark retained so existing links still open and canonicalise.
  '/resource-library',
  '/billing',
  '/client-management',
  // AuthenticatedController writes /membership when it cannot resolve the
  // company name, and /membership/[slug] when it can.
  '/membership',
  '/businesses',
  // OAuth landing
  '/xero-callback',
  '/mcp/authorize',
  '/mcp/connections',
]);

/** Prefixes whose tail is opaque to us — a token, a provider callback, a
 *  reference the feature itself validates. Matching the prefix is as far as
 *  this can honestly go. */
const PREFIX_ROUTES: readonly string[] = [
  '/auth/',
  '/wallet/verify/',
  '/daywork-sign/',
  '/appointment/',
  '/shared/',
  '/workflows/run/',
];

/** Parameterised shapes, mirroring the parsers in prettyRoutes.ts. Kept as
 *  patterns rather than calls to those parsers because several of them reject
 *  reserved slugs (`/employers/add`, `/workspaces/add-workspace`) that ARE
 *  real routes — they are handled above, and this only needs the shape. */
const PATTERN_ROUTES: readonly RegExp[] = [
  // Legacy fallback written while a project route is waiting for the local
  // database snapshot to hydrate. AuthenticatedController still parses this
  // exact UUID shape, so a release-triggered reload must not turn it into a
  // top-level 404.
  /^\/projects\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\/?$/i,
  // /projects/[workspace]/[project]
  /^\/projects\/[^/]+\/[^/]+\/?$/,
  // /projects/[workspace]/[project]/[tab]
  /^\/projects\/[^/]+\/[^/]+\/[^/]+\/?$/,
  // /projects/[workspace]/[project]/pricing/[bill]
  /^\/projects\/[^/]+\/[^/]+\/pricing\/[^/]+\/?$/,
  // /projects/[workspace]/[project]/pricing/[bill]/export
  /^\/projects\/[^/]+\/[^/]+\/pricing\/[^/]+\/export\/?$/,
  /^\/projects\/[^/]+\/[^/]+\/notebook(?:\/(?:new|pages\/[a-z0-9]+(?:-[a-z0-9]+)*))?(?:\/tasks)?\/?$/,
  // Procurement owns focused pages under its project tab.
  /^\/projects\/[^/]+\/[^/]+\/procurement\/(?:resources|enquiries|quotes|awards|orders)\/?$/,
  // Reports names the open report under its project tab.
  /^\/projects\/[^/]+\/[^/]+\/reports\/[a-z-]+\/?$/,
  // Admin owns focused setup pages under its project tab.
  /^\/projects\/[^/]+\/[^/]+\/admin\/(?:appointment|contract|payment|dates|activity)\/?$/,
  // /projects/[workspace]/[project]/documents/[document]
  /^\/projects\/[^/]+\/[^/]+\/documents\/[^/]+\/?$/,
  // /projects/[workspace]/[project]/takeoff/[sheet]
  /^\/projects\/[^/]+\/[^/]+\/takeoff\/[^/]+\/?$/,
  // /projects/[workspace]/[project]/events/[...tail] — the tail is multi-segment
  // (a reference, or a reference plus a form kind), so it stays open-ended.
  /^\/projects\/[^/]+\/[^/]+\/events\/.+$/,
  // /projects/[workspace]/[project]/team/[member]
  /^\/projects\/[^/]+\/[^/]+\/team\/[^/]+\/?$/,
  // /calendar/tasks and /calendar/tasks/[task]; the older /tasks/[task] still parses
  /^\/calendar\/tasks\/?$/,
  /^\/(?:calendar\/)?tasks\/[^/]+\/?$/,
  // /contacts/[name--stable-key] (a person) and /contacts/[company-slug][/section] (a company)
  /^\/contacts\/[^/]+(?:\/[^/]+)?\/?$/,
  // The workspace's own documents name the open folder too: /profile/workspace/documents/[folder]/...
  /^\/profile\/workspace\/documents\/.+$/,
  // ...and a company's documents name the open folder: /contacts/[company]/documents/[folder]/...
  /^\/contacts\/[^/]+\/documents\/.+$/,
  // /workspaces/[slug], its canonical focused section, and the legacy inline
  // editor address retained for old bookmarks.
  /^\/workspaces\/[^/]+\/?$/,
  /^\/workspaces\/[^/]+\/(?:trade-profile|sales|purchase-orders|subscription|membership|invoices|credit-report|integrations|documents|projects|users|activity|ai-agents)\/?$/,
  /^\/workspaces\/[^/]+\/edit\/?$/,
  // Legacy workspace addresses still parsed by parsePrettyWorkspacePath
  /^\/workspaces\/profile\/[^/]+\/?$/,
  /^\/(?:businesses|clients)\/[^/]+\/?$/,
  // /employers/[slug] and /employers/[slug]/[section]
  /^\/employers\/[^/]+(?:\/[^/]+)?\/?$/,
  // /account/[workspace]/[user] and an optional personal-settings section.
  // Both identifiers are readable slugs; internal UUIDs never enter the URL.
  /^\/account\/[^/]+\/[^/]+(?:\/[^/]+)?\/?$/,
  // Workspace-scoped finance hubs: /[hub]/[workspace]/[section].
  /^\/(?:payroll|accounting|tax)\/[^/]+\/[^/]+\/?$/,
  // Views that carry their open sub-route: /sales/[section],
  // /system-settings/[tab], /profile/[tab]. Read off the registry rather than
  // restated here — the hand-written version accepted a single segment, so the
  // nested settings pages the parser has always handled (/system-settings/
  // integrations/xero and 13 others) were 404'd by this table.
  ...registeredSubRoutePatterns(),
  // /membership/[company-slug]
  /^\/membership\/[^/]+\/?$/,
  // /[workspace]/resource-library
  /^\/[^/]+\/resource-library\/?$/,
  // /[workspace]/resource-library/libraries/[name--id]/items/new|[name--id]
  /^\/[^/]+\/resource-library\/libraries\/[^/]+\/items\/[^/]+\/?$/,
  // The rate-item editor, pushed by pushItemUrl in resource-library/utils.ts:
  // /resource-library/[library-slug]/items/new | edit
  /^\/resource-library\/[^/]+\/items\/(?:new|edit)\/?$/,
];

/** Trailing slash and case are not meaningful in this app's URLs. */
/** Reporting left the main rail for each project's Reports tab; old links go Home. */
export function isLegacyReportingPath(pathname: string): boolean {
  return /^\/reports-insights(?:\/|$)/i.test(pathname);
}

function normalise(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return (trimmed || '/').toLowerCase();
}

export function matchesKnownRoute(pathname: string): boolean {
  if (!pathname) return false;
  const path = normalise(pathname);
  if (parsePrettyProjectInterimPath(path)) return true;
  if (/^\/projects\/[^/]+\/[^/]+\/(?:valuations|interim)\//.test(path)) return false;
  // Claims and event details share a module but have distinct route grammars.
  if (/^\/projects\/[^/]+\/[^/]+\/changes(?:\/|$)/.test(path)) {
    return Boolean(parsePrettyProjectEventsPath(path) || parsePrettyProjectTabPath(path));
  }

  if (VIEW_ROUTES.has(path)) return true;
  if (STATIC_ROUTES.has(path)) return true;
  // The tail is REQUIRED, so a bare prefix does not match: `/shared` with no
  // token is not a share link, it is a dead URL.
  if (PREFIX_ROUTES.some(prefix => path.startsWith(normalise(prefix) + '/'))) return true;
  return PATTERN_ROUTES.some(pattern => pattern.test(path));
}

/** The inverse, named for the call site's intent. */
export function isUnknownRoute(pathname: string): boolean {
  return !matchesKnownRoute(pathname);
}
